import { ActionFlowError } from '../types.action-flow'

/**
 * True for the sentinel `raceAbort` throws when its signal fires. Callers with
 * fail-open `catch` blocks must rethrow this instead of swallowing it, so a
 * reset/unmount mid-operation still propagates to the provider's abort branch.
 */
export function isExecutionAbortError(error: unknown): error is ActionFlowError {
  return error instanceof ActionFlowError && error.message === 'Execution aborted'
}

/**
 * Race a viem (or any) promise against an `AbortSignal`. On abort, reject
 * with a non-reportable `ActionFlowError` so the provider's catch branch
 * swallows it. The original promise is allowed to resolve/reject in the
 * background — we attach a no-op `.catch` so a late rejection (e.g.
 * `waitForCallsStatus` "No matching bundle found", or `waitForTransactionReceipt`
 * timing out after the user closed the dialog) does not bubble to
 * `window.onunhandledrejection` and trip the error reporter after the user
 * has abandoned the flow.
 *
 * Used by the execution loop to make every viem `await` interruptible by
 * `controller.abort()` on reset/unmount, including waits that would otherwise
 * sit blocked on viem's internal polling for minutes.
 */
export function raceAbort<T>(promise: Promise<T>, signal: AbortSignal | undefined): Promise<T> {
  if (!signal) return promise
  if (signal.aborted) {
    promise.catch(() => {})
    return Promise.reject(new ActionFlowError('Execution aborted', { reportable: false }))
  }
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      promise.catch(() => {})
      reject(new ActionFlowError('Execution aborted', { reportable: false }))
    }
    signal.addEventListener('abort', onAbort, { once: true })
    promise.then(
      v => {
        signal.removeEventListener('abort', onAbort)
        resolve(v)
      },
      e => {
        signal.removeEventListener('abort', onAbort)
        reject(e)
      }
    )
  })
}
